import { Helmet } from 'react-helmet-async';

interface EnhancedSEOProps {
  title: string;
  description: string;
  keywords: string;
  canonicalUrl: string;
  ogType?: 'website' | 'article';
  publishDate?: string;
  modifiedDate?: string;
  author?: string;
  section?: string;
  tags?: string[];
  imageUrl?: string;
  structuredData?: object;
}

const truncateTitle = (title: string): string => {
  // Google typically displays 50-60 characters
  return title.length > 60 ? title.substring(0, 57) + '...' : title;
};

const truncateDescription = (desc: string): string => {
  // Google typically displays 155-160 characters
  return desc.length > 155 ? desc.substring(0, 152) + '...' : desc;
};

export function EnhancedSEO({
  title,
  description,
  keywords,
  canonicalUrl,
  ogType = 'website',
  publishDate,
  modifiedDate,
  author = 'D. Toutant',
  section = 'Cryptocurrency',
  tags = [],
  imageUrl = 'https://mdbapp.io/preview.jpg',
  structuredData
}: EnhancedSEOProps) {
  const optimizedTitle = truncateTitle(title);
  const optimizedDescription = truncateDescription(description);

  const defaultStructuredData = {
    "@context": "https://schema.org",
    "@type": ogType === 'article' ? 'Article' : 'WebPage',
    "headline": optimizedTitle,
    "description": optimizedDescription,
    "image": imageUrl,
    "author": {
      "@type": "Person",
      "name": author,
      "url": "https://mdbapp.io/authors/dtdevmdbappio"
    },
    "publisher": {
      "@type": "Organization",
      "name": "MDB App",
      "logo": {
        "@type": "ImageObject",
        "url": "https://mdbapp.io/logo.png"
      }
    },
    "datePublished": publishDate,
    "dateModified": modifiedDate || publishDate,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": canonicalUrl
    }
  };

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{optimizedTitle}</title>
      <meta name="description" content={optimizedDescription} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={canonicalUrl} />
      <meta name="author" content={author} />
      <meta name="robots" content="index, follow, max-image-preview:large" />
      
      {/* OpenGraph Meta Tags */}
      <meta property="og:title" content={optimizedTitle} />
      <meta property="og:description" content={optimizedDescription} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content="MDB App" />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      
      {/* Article Specific Tags */}
      {ogType === 'article' && (
        <>
          <meta property="article:published_time" content={publishDate} />
          <meta property="article:modified_time" content={modifiedDate || publishDate} />
          <meta property="article:author" content={`https://mdbapp.io/authors/${author.toLowerCase().replace(/\s+/g, '-')}`} />
          <meta property="article:section" content={section} />
          {tags.map(tag => (
            <meta key={tag} property="article:tag" content={tag} />
          ))}
        </>
      )}
      
      {/* Twitter Card Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={optimizedTitle} />
      <meta name="twitter:description" content={optimizedDescription} />
      <meta name="twitter:image" content={imageUrl} />
      <meta name="twitter:creator" content="@mdbappio" />
      
      {/* Mobile Optimization */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=5.0" />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="theme-color" content="#0a74ff" />
      
      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": "MDBApp Token Creator",
          "url": "https://mdbapp.io",
          "potentialAction": {
            "@type": "SearchAction",
            "target": "https://mdbapp.io/search?q={search_term_string}",
            "query-input": "required name=search_term_string"
          },
          "sameAs": [
            "https://dappradar.com/dapp/mdbapp"
          ],
          "mentions": [
            {
              "@type": "Organization",
              "name": "DappRadar",
              "url": "https://dappradar.com",
              "description": "MdbApp is featured on DappRadar, the leading dApp analytics platform"
            }
          ]
        })}
      </script>
      
      {/* Additional Performance Hints */}
      <link rel="preconnect" href="https://mdbapp.io" />
      <link rel="dns-prefetch" href="https://mdbapp.io" />
    </Helmet>
  );
}
